import { Link } from "react-router-dom";
import { Check, AlertTriangle, Minus, ListChecks } from "lucide-react";
import type { DepartureFulfillment, FulfillmentCell, ReadinessState } from "@ckgo2/shared/types";

/**
 * Per-departure fulfillment grid (Block 4.3, Téma 3) — the self-filling replacement for the PAX
 * Excel. Rows = orders, columns = derived states (deposit/balance/passport/insurance/visa/contract)
 * + rooming roll-up + a "customized" flag. Almost every cell is DERIVED from facts (payments,
 * service bookings, contract status), so the team reads it instead of ticking boxes. The data comes
 * straight from the velín response (`d.fulfillment`); this component is pure presentation.
 */

const STATE_STYLE: Record<ReadinessState, string> = {
  done: "text-emerald-600 dark:text-emerald-400",
  warn: "text-amber-500",
  todo: "text-gray-300 dark:text-gray-600",
  na: "text-gray-200 dark:text-gray-700",
};

function cellTitle(label: string, cell: FulfillmentCell): string {
  if (cell.state === "na") return `${label}: not yet relevant`;
  const count = cell.total != null ? ` (${cell.done ?? 0}/${cell.total})` : "";
  const word = cell.state === "done" ? "done" : cell.state === "warn" ? "needs attention" : "to do";
  return `${label}: ${word}${count}`;
}

function Cell({ label, cell, count }: { label: string; cell: FulfillmentCell; count?: boolean }) {
  const icon =
    cell.state === "done" ? (
      <Check className="h-4 w-4" />
    ) : cell.state === "warn" ? (
      <AlertTriangle className="h-4 w-4" />
    ) : (
      <Minus className="h-4 w-4" />
    );
  const showCount = count && cell.total != null && cell.state !== "na";
  return (
    <td className="px-2 py-2 text-center" title={cellTitle(label, cell)}>
      <span
        className={`inline-flex items-center justify-center gap-0.5 ${STATE_STYLE[cell.state]}`}
      >
        {icon}
        {showCount && (
          <span className="text-[10px] font-medium">
            {cell.done}/{cell.total}
          </span>
        )}
      </span>
    </td>
  );
}

const COLS: { key: keyof DepartureFulfillment["rows"][number]; label: string; count?: boolean }[] =
  [
    { key: "deposit", label: "Deposit" },
    { key: "balance", label: "Balance" },
    { key: "passport", label: "Passport", count: true },
    { key: "insurance", label: "Insurance", count: true },
    { key: "visa", label: "Visa", count: true },
    { key: "contract", label: "Contract" },
  ];

export default function FulfillmentGrid({ data }: { data: DepartureFulfillment }) {
  const { rows, aggregate } = data;

  const warnings: string[] = [];
  if (aggregate.missingDeposit) warnings.push(`${aggregate.missingDeposit} deposit pending`);
  if (aggregate.missingBalance) warnings.push(`${aggregate.missingBalance} balance pending`);
  if (aggregate.missingPassport)
    warnings.push(`${aggregate.missingPassport} with missing passports`);
  if (aggregate.passportExpiring)
    warnings.push(
      `${aggregate.passportExpiring} passport${aggregate.passportExpiring > 1 ? "s" : ""} expiring soon`,
    );
  if (aggregate.missingInsurance) warnings.push(`${aggregate.missingInsurance} without insurance`);
  if (aggregate.missingVisa) warnings.push(`${aggregate.missingVisa} visa pending`);
  if (aggregate.missingContract) warnings.push(`${aggregate.missingContract} contract unsigned`);

  return (
    <div className="card">
      <h2 className="mb-4 flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-white">
        <ListChecks className="h-5 w-5" /> Fulfillment
        <span className="text-sm font-normal text-gray-400">({rows.length})</span>
      </h2>

      {rows.length === 0 ? (
        <p className="text-sm text-gray-400">No live orders on this departure yet.</p>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-gray-200 text-xs uppercase text-gray-500 dark:border-gray-700">
                <tr>
                  <th className="px-2 py-2 text-left font-medium">Order</th>
                  <th className="px-2 py-2 text-left font-medium">Pax</th>
                  {COLS.map((c) => (
                    <th key={c.key} className="px-2 py-2 text-center font-medium">
                      {c.label}
                    </th>
                  ))}
                  <th className="px-2 py-2 text-left font-medium">Room</th>
                  <th className="px-2 py-2 text-center font-medium" title="Has a customization">
                    ≠
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {rows.map((r) => (
                  <tr key={r.orderId}>
                    <td className="px-2 py-2">
                      <Link
                        to={`/orders/${r.orderId}`}
                        className="text-primary-600 dark:text-primary-400 font-mono hover:underline"
                      >
                        {r.orderNumber ?? "—"}
                      </Link>
                      <div className="text-xs text-gray-500">{r.leadName}</div>
                    </td>
                    <td className="px-2 py-2 text-gray-600 dark:text-gray-300">{r.pax}</td>
                    {COLS.map((c) => (
                      <Cell
                        key={c.key}
                        label={c.label}
                        cell={r[c.key] as FulfillmentCell}
                        count={c.count}
                      />
                    ))}
                    <td className="px-2 py-2 font-mono text-xs text-gray-600 dark:text-gray-300">
                      {r.rooms || "—"}
                    </td>
                    <td className="px-2 py-2 text-center">
                      {r.customized ? (
                        <span
                          className="bg-primary-100 text-primary-700 inline-block rounded px-1.5 py-0.5 text-[10px] font-medium"
                          title="This order has an addon/custom line"
                        >
                          custom
                        </span>
                      ) : (
                        <span className="text-gray-300 dark:text-gray-600">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {warnings.length > 0 && (
            <div className="bg-warning-50 text-warning-700 dark:bg-warning-900/20 dark:text-warning-400 mt-3 flex items-start gap-2 rounded-lg p-2 text-xs">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>{warnings.join(" · ")}</span>
            </div>
          )}
        </>
      )}
    </div>
  );
}
